import React from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, Users, TrendingUp, Heart } from 'lucide-react';
import { Card } from '../components/ui/card';

const Careers = () => {
  const values = [
    {
      icon: Users,
      title: 'Customer First',
      description: 'We exist to serve HENWYs—professionals who deserve better than robo-advisors but can\'t access private wealth management. Every decision starts with: "Does this help our customers build wealth?"'
    },
    {
      icon: TrendingUp,
      title: 'Innovation Over Tradition',
      description: 'The wealth industry is stuck in the 1980s. We leverage AI, automation, and modern technology to deliver institutional-grade investing at consumer prices.'
    },
    {
      icon: Heart,
      title: 'Mission-Driven',
      description: 'We\'re reshaping how future generations invest. Our goal isn\'t just profit—it\'s democratizing access to the investments that build generational wealth.'
    },
    {
      icon: Briefcase,
      title: 'Transparency Always',
      description: 'No hidden fees. No jargon. No gatekeeping. We believe financial services should be transparent, fair, and accessible to everyone.'
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F3ED', paddingTop: '80px' }}>
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-6xl md:text-7xl font-light mb-6 leading-tight" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
            Careers at Trove
          </h1>
          <p className="text-xl text-gray-600 font-light max-w-3xl mx-auto">
            Join us in reshaping how Australians build wealth. We're building the future of investment management.
          </p>
        </div>
      </section>

      {/* Current Status */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Card className="p-8 border-0 shadow-lg text-center">
            <div className="mb-6">
              <Briefcase size={64} className="mx-auto" style={{ color: '#FF8C42' }} />
            </div>
            <h2 className="text-3xl font-light mb-4" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
              No Open Positions Right Now
            </h2>
            <p className="text-gray-600 font-light leading-relaxed mb-6">
              We're not actively hiring at the moment, but we're always interested in connecting with talented individuals who share our mission. If you're passionate about democratizing wealth management and building technology that makes a difference, we'd love to hear from you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="mailto:careers@trove.com.au" 
                className="inline-block px-8 py-3 bg-[#FF8C42] text-white rounded-full hover:bg-[#FFB366] transition-colors font-medium"
              >
                Email Us Your Resume
              </a>
              <a 
                href="https://www.linkedin.com/company/trove-wealth" 
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-8 py-3 border-2 border-[#1a1a3e] text-[#1a1a3e] rounded-full hover:bg-[#1a1a3e] hover:text-white transition-colors font-medium"
              >
                Follow Us on LinkedIn
              </a>
            </div>
          </Card>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-light mb-12 text-center" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
            What We Stand For
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="p-8 border-0 shadow-sm">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <IconComponent className="text-[#FF8C42]" size={28} />
                    </div>
                    <h3 className="text-xl font-light flex-1" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
                      {value.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 font-light leading-relaxed">
                    {value.description}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Trove */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F5F3ED' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-light mb-8 text-center" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
            Why Join Trove?
          </h2>
          
          <div className="bg-white rounded-lg p-8 shadow-sm">
            <div className="space-y-6 text-gray-700 leading-relaxed">
              <div>
                <h3 className="text-xl font-semibold mb-2" style={{ color: '#1a1a3e' }}>Impact-Driven Work</h3>
                <p>
                  You'll be building technology that helps thousands of Australians transition from high income to high net worth. Real impact. Real results.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-2" style={{ color: '#1a1a3e' }}>Cutting-Edge Technology</h3>
                <p>
                  Work with modern tech stacks, AI-powered portfolio optimization, and fintech infrastructure that's reshaping wealth management.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-2" style={{ color: '#1a1a3e' }}>Autonomy & Ownership</h3>
                <p>
                  We hire smart people and trust them to do great work. You'll have real ownership over your projects and the freedom to innovate.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-2" style={{ color: '#1a1a3e' }}>Competitive Compensation</h3>
                <p>
                  Market-leading salaries, equity participation, and benefits that reflect the value you bring to the team.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-light mb-6" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
            Interested in Future Opportunities?
          </h2>
          <p className="text-gray-600 font-light mb-8 max-w-2xl mx-auto">
            Even though we're not hiring right now, we're always open to conversations with exceptional talent. Send us your resume and we'll keep you in mind for future roles.
          </p>
          <p className="text-gray-600">
            <strong>Email:</strong> <a href="mailto:careers@trove.com.au" className="text-[#FF8C42] hover:text-[#FFB366]">careers@trove.com.au</a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default Careers;